import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { customerQueries, queryClient } from '@as-react/common-queries';
import { AuthStatusCode, EmailValidationResponseStatus } from '@as-react/common-types';
import { debouncedPromise } from '@as-react/common-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckCircleFillIcon } from '@yonderland/andes-icons';
import { Banner, ButtonAlpha, Icon, Stack, TextAlpha, TextField } from '@yonderland/andes-react';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useGuestLogin } from '../../../../hooks/useGuestLogin';
import FormValidationUtil from '../../../../util/FormValidationUtil';
import { EMAIL_VALIDATION_DEBOUNCE, validateEmailWithBackend } from '../../../email/EmailValidationUtil';
import { FormEmailError } from '../../../form/FormEmailError';
import { FormField } from '../../../form/FormField';
const defaultCardInfo = {
    customerUUID: null,
    email: null,
    validEmail: false,
    existingCustomer: false,
};
export const ShoplandingLogin = () => {
    var _a, _b;
    const { defaultRequestParams, servicesEndpoint, checkoutLoyaltyCardPrefix } = useAemConfig();
    const { t } = useTranslation([I18N_NAMESPACES.checkout, I18N_NAMESPACES.buy, I18N_NAMESPACES.account]);
    const [emailOrCardCodeStatus, setEmailOrCardCodeStatus] = useState();
    const [linkedEmailStatus, setLinkedEmailStatus] = useState();
    const [suggestedEmail, setSuggestedEmail] = useState();
    const { guestLogin, errorMessage, isPending: isGuestLoginPending } = useGuestLogin();
    const errorFeedback = errorMessage ? t(errorMessage) : undefined;
    const { getCardNumberExists, getEmailExists } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const getCustomerInfoForExploreMoreCard = (customerCardNumber) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield queryClient.fetchQuery(getCardNumberExists(customerCardNumber));
        }
        catch (_a) {
            return defaultCardInfo;
        }
    });
    const getEmailStatus = (email) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield queryClient.fetchQuery(getEmailExists({ email }));
        }
        catch (_a) {
            return { accountStatusCode: AuthStatusCode.NO_ACCOUNT, knownForFascia: false, hasIdentity: false };
        }
    });
    const validateEmailOrCardCode = useCallback(debouncedPromise((emailOrCardCode, context) => __awaiter(void 0, void 0, void 0, function* () {
        if (formProps.watch('skipEmailValidation')) {
            return true;
        }
        if (emailOrCardCode) {
            const { isValidFormat, isDefaultCardNumber } = FormValidationUtil.isValidExploreMoreCardFormat(emailOrCardCode, checkoutLoyaltyCardPrefix);
            if (isValidFormat && isDefaultCardNumber) {
                return context.createError({
                    message: t(`${I18N_NAMESPACES.checkout}:checkout.login.error.loyalty.card.default`),
                });
            }
            else if (isValidFormat && !isDefaultCardNumber) {
                const cardInfo = yield getCustomerInfoForExploreMoreCard(emailOrCardCode);
                formProps.setValue('customerCardInfo', cardInfo);
                if (!cardInfo.existingCustomer) {
                    formProps.resetField('linkedEmail');
                    return context.createError({
                        message: t(`${I18N_NAMESPACES.checkout}:checkout.login.error.loyalty.card`),
                    });
                }
                if (!formProps.getFieldState('linkedEmail').isDirty) {
                    formProps.setValue('linkedEmail', cardInfo.validEmail ? cardInfo.email : '');
                }
            }
            else if (!isValidFormat) {
                formProps.resetField('linkedEmail');
                formProps.setValue('skipEmailValidation', false, { shouldValidate: false });
                if (!FormValidationUtil.isValidEmailPattern(emailOrCardCode)) {
                    return context.createError({ message: t(`${I18N_NAMESPACES.buy}:buy.error.invalid`) });
                }
                else {
                    return yield validateEmailWithBackend(emailOrCardCode, context, servicesEndpoint, emailOrCardCodeStatus, setSuggestedEmail, setEmailOrCardCodeStatus);
                }
            }
        }
        else if (!emailOrCardCode) {
            return context.createError({ message: t(`${I18N_NAMESPACES.buy}:buy.login.error.required`) });
        }
        return true;
    }), EMAIL_VALIDATION_DEBOUNCE), []);
    const validateLinkedEmail = useCallback(debouncedPromise((linkedEmail, context) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (formProps.watch('skipEmailValidation')) {
            return true;
        }
        if (!linkedEmail) {
            return context.createError({ message: t(`${I18N_NAMESPACES.buy}:buy.login.email.error.required`) });
        }
        else if (!FormValidationUtil.isValidEmailPattern(linkedEmail)) {
            return context.createError({
                message: EmailValidationResponseStatus.INVALID,
            });
        }
        else if (((_a = formProps.watch('customerCardInfo')) === null || _a === void 0 ? void 0 : _a.email) !== linkedEmail) {
            const { hasIdentity } = yield getEmailStatus(linkedEmail);
            if (hasIdentity) {
                return context.createError({
                    message: t(`${I18N_NAMESPACES.checkout}:checkout.login.error.known.user`),
                });
            }
            return yield validateEmailWithBackend(linkedEmail, context, servicesEndpoint, linkedEmailStatus, setSuggestedEmail, setLinkedEmailStatus);
        }
        return true;
    }), EMAIL_VALIDATION_DEBOUNCE), []);
    const handleAcceptSuggestedEmailClick = () => {
        var _a;
        if ((_a = formProps.watch('customerCardInfo')) === null || _a === void 0 ? void 0 : _a.existingCustomer) {
            setLinkedEmailStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
            formProps.setValue('linkedEmail', suggestedEmail, { shouldValidate: true });
        }
        else {
            setEmailOrCardCodeStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
            formProps.setValue('emailOrCardCode', suggestedEmail, { shouldValidate: true });
        }
    };
    const handleIgnoreSuggestedEmailClick = () => {
        formProps.setValue('skipEmailValidation', true);
        formProps.clearErrors(['emailOrCardCode', 'linkedEmail']);
    };
    const validationSchema = Yup.object({
        emailOrCardCode: Yup.string().default('').test(validateEmailOrCardCode),
        customerCardInfo: Yup.object({
            customerUUID: Yup.string().nullable(),
            email: Yup.string().nullable(),
            existingCustomer: Yup.boolean(),
            validEmail: Yup.boolean(),
        }).optional(),
        skipEmailValidation: Yup.boolean().default(false),
        linkedEmail: Yup.string()
            .default('')
            .when('customerCardInfo', {
            is: (customerCardInfo) => customerCardInfo === null || customerCardInfo === void 0 ? void 0 : customerCardInfo.existingCustomer,
            then: schema => schema.test(validateLinkedEmail),
            otherwise: schema => schema.optional(),
        }),
    });
    const renderError = (msg) => (_jsx(TextAlpha, { variant: "bodyS", children: _jsx(FormEmailError, { emailStatus: msg, suggestedEmail: suggestedEmail, requiredErrorLabel: t(`${I18N_NAMESPACES.buy}:buy.login.email.error.required`), onAcceptClick: handleAcceptSuggestedEmailClick, onIgnoreClick: handleIgnoreSuggestedEmailClick }) }));
    const handleShopLoginSubmit = ({ linkedEmail, emailOrCardCode, customerCardInfo }) => {
        var _a;
        if (FormValidationUtil.isValidExploreMoreCardFormat(emailOrCardCode, checkoutLoyaltyCardPrefix).isValidFormat &&
            !linkedEmail)
            return;
        if (customerCardInfo === null || customerCardInfo === void 0 ? void 0 : customerCardInfo.existingCustomer) {
            guestLogin(linkedEmail, emailOrCardCode, (_a = customerCardInfo.customerUUID) !== null && _a !== void 0 ? _a : undefined);
        }
        else {
            guestLogin(emailOrCardCode);
        }
    };
    const formProps = useForm({
        defaultValues: { emailOrCardCode: '', linkedEmail: '', customerCardInfo: undefined, skipEmailValidation: false },
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
    });
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleShopLoginSubmit), noValidate: true, name: "login-form", children: _jsxs(Stack, { direction: "col", gap: "6", children: [_jsx(TextAlpha, { variant: "headingS", color: "emphasised", children: t(`${I18N_NAMESPACES.buy}:buy.shop.login.title`) }), errorFeedback && _jsx(Banner, { text: errorFeedback, status: "error" }), ((_a = formProps.watch('customerCardInfo')) === null || _a === void 0 ? void 0 : _a.existingCustomer) && (_jsx(Banner, { text: t(`${I18N_NAMESPACES.checkout}:checkout.login.card.link.email`), status: "informative" })), _jsx(FormField, { name: "emailOrCardCode", required: true, renderError: renderError, children: (_a) => {
                            var _b;
                            var _c = _a.fieldProps, { ref, name } = _c, options = __rest(_c, ["ref", "name"]), { error } = _a;
                            return (_jsx(TextField, Object.assign({}, options, { label: t(`${I18N_NAMESPACES.buy}:buy.shop.login.email.or.customer.card`), name: name, id: name, error: error, adornmentEnd: ((_b = formProps.watch('customerCardInfo')) === null || _b === void 0 ? void 0 : _b.existingCustomer) && !error ? (_jsx(Icon, { source: CheckCircleFillIcon, color: "success" })) : undefined, type: "email" })));
                        } }), ((_b = formProps.watch('customerCardInfo')) === null || _b === void 0 ? void 0 : _b.existingCustomer) && (_jsx(FormField, { name: "linkedEmail", required: true, renderError: renderError, children: (_a) => {
                            var _b = _a.fieldProps, { ref, name } = _b, options = __rest(_b, ["ref", "name"]), { error } = _a;
                            return (_jsx(TextField, Object.assign({}, options, { label: t(`${I18N_NAMESPACES.account}:account.flyout.email.placeholder`), placeholder: t(`${I18N_NAMESPACES.account}:account.flyout.email.placeholder`), name: name, id: name, error: error, type: "email" })));
                        } })), _jsx(ButtonAlpha, { testId: "login_btn", type: "submit", theme: "neutral", state: isGuestLoginPending ? 'loading' : 'interactive', fullWidth: true, children: t(`${I18N_NAMESPACES.buy}:buy.shop.login.submit`) })] }) }) })));
};
